
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { Website, WebsiteDetails } from '@/services/Website/interfaces';
import WPCliRepositoryInterface, { WPCheckResultsInterface } from '@/services/WPCli/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import InstallWPForm from '@/services/WPCli/forms/InstallWPForm';
import { DatabaseInterface, DatabaseRepositoryInterface, DatabaseStatus } from '@/services/Database/interfaces';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import { OriginVhostPHPModuleInterface } from "@/services/Vhost/Origin/Interfaces";
import { FormValidationFunction } from "@/views/interfaces/vuetify/form.interface";

@Component({
  mixins: [Themeable],
  inject: [
    'wpCliRepository',
    'databaseRepository',
    'systemUserRepository',
  ],
  sockets: {
    wpCheckResult(msg: { payload: WPCheckResultsInterface }): void {
      this.$emit('resultsReceived', msg.payload);
    },
  },
})
export default class WpInstallDialog extends Vue {
  @Prop() public website: Website;

  public visible: boolean = false;

  public loading: boolean = true;

  public readonly wpCliRepository!: WPCliRepositoryInterface;

  public readonly databaseRepository!: DatabaseRepositoryInterface;

  public readonly systemUserRepository!: SystemUserRepositoryInterface;

  public databases: DatabaseInterface[] = [];

  public systemUser!: SystemUserInterface;

  public alreadyInstalled: boolean = false;

  public availablePlugins: { name: string, value:string}[] = [
    {name:"WooCommerce", value:"wc"},
    {name:"W3 Total Cache", value:"w3tc"},
  ]

  public form: WPXFormValue<InstallWPForm> = new WPXFormValue(InstallWPForm);

  public async created(): Promise<void> {
    this.visible = true;
    this.$on('resultsReceived', this.handleWpCheckResults);

    await this.getSystemUser(this.website.details.unixUserId);
    await this.getDatabases(this.website.id);

    try {
      await this.wpCliRepository.getWPStatus(this.website.id);
    } catch (err) {
      const errMessage = err?.response?.data?.error || err.message;
      this.$errorDialog.show(`WP Installation Check Request Failed. . . ${errMessage}`);
    }
  }

  public handleWpCheckResults(data: WPCheckResultsInterface): void {
    if (Number(data.websiteId) !== this.website.id) {
      return;
    }


    const wpInstalled = data.wordpressStatus;
    const otherInstallation = data.otherWebsiteStatus;

    this.alreadyInstalled = wpInstalled || otherInstallation;

    if (!this.alreadyInstalled) {
      this.form.data.forceInstall = true;
    }

    this.loading = false;
  }

  public async getSystemUser(systemUserId: number): Promise<void> {
    const systemUser = await this.systemUserRepository.findSystemUserById(systemUserId);
    if (!systemUser) {
      throw new Error(`System user with website id: ${systemUserId} not found!`);
    }

    this.systemUser = systemUser;
  }

  public async getDatabases(websiteId: number): Promise<void> {
    try {
      const databases = await this.databaseRepository.getByWebsiteId(websiteId);

      this.databases = databases.filter((database) => database.status === DatabaseStatus.CREATED);
    } catch (err) {
      const errMessage = err?.response?.data?.error || err.message;
      this.$errorDialog.show(`Databases Request Failed. . . ${errMessage}`);
    }
  }

  public async install(): Promise<void> {
    try {
      await this.wpCliRepository.installWp({
        title: this.form.data.title,
        user: this.form.data.user,
        password: this.form.data.password,
        email: this.form.data.email,
        databaseId: this.form.data.databaseId,
        websiteId: this.website.id,
        forceInstall: true,
        directory: this.form.data.directory,
        enableRedisCache: this.form.data.enableRedisCache,
        plugins: this.form.data.selectedPlugins,
      });

      this.$successDialog.show('WP Installation Started');
      this.destroyComponent();
    } catch (err) {
      const errMessage = err?.response?.data?.error || err.message;
      this.$errorDialog.show(`Install WP Request Failed. . . ${errMessage}`);
    }
  }

  public getDirectory(
    websiteId: number,
    systemUserWebsiteId: number,
    systemUserDirectory: string,
    domain: string,
  ): string {
    const rootDirectory = (websiteId === systemUserWebsiteId ?
      systemUserDirectory : `${systemUserDirectory}/domains/${domain}`).replace(/\/\//gm, '/');

    return `${rootDirectory}/public_html`;
  }

  public destroyComponent(): void {
    this.visible = false;
    this.$emit('close');
  }
}
